import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBars,
  faChevronDown,
  faEdit,
  faBan,
  faChevronLeft,
  faChevronRight,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import UserModal from '../components/UserModal';
import Modal from '../components/Modal';

function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToInactivate, setUserToInactivate] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Add error boundary for FontAwesome icons
  const [iconsReady, setIconsReady] = useState(false);

  useEffect(() => {
    // Ensure DOM is ready before rendering icons
    const timer = setTimeout(() => {
      setIconsReady(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Store admin session when component mounts
    const storeAdminSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        localStorage.setItem('adminSession', JSON.stringify(session));
      }
    };

    storeAdminSession();
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup admin session when component unmounts
      localStorage.removeItem('adminSession');
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log(user);
      // First, get the current user's role

      // Only fetch users if the current user is an admin
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      filters.search === '' ||
      user.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesStatus =
      filters.status === '' || user.status === filters.status;

    return matchesSearch && matchesStatus;
  });

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle change in number of users per page
  const handleUsersPerPageChange = (e) => {
    setUsersPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters or usersPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, usersPerPage]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const openUserModal = (userId = null) => {
    setSelectedUser(userId ? users.find((u) => u.id === userId) : null);
    setIsModalOpen(true);
  };

  const openInactivateDialog = (userId) => {
    setUserToInactivate(userId);
    setShowConfirmDialog(true);
  };

  const handleInactivate = async () => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ status: 'inactive' })
        .eq('id', userToInactivate);

      if (error) throw error;
      await fetchUsers();
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Error inactivating user:', error);
      alert('Failed to inactivate user');
    }
  };

  const handleSaveUser = async () => {
    await fetchUsers(); // Refresh the list after saving
    setIsModalOpen(false);
  };

  const openDeleteDialog = (userId) => {
    setUserToDelete(userId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      // First delete from auth system using admin API
      const { error: authError } =
        await supabaseAdmin.auth.admin.deleteUser(userToDelete);
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userToDelete);

      if (authError) throw authError;

      // Then delete from our users table
      const { error: dbError } = await supabase
        .from('users')
        .delete()
        .eq('id', userToDelete);

      if (dbError) throw dbError;

      await fetchUsers();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {iconsReady && (
                <button className="text-gray-500 lg:hidden">
                  <FontAwesomeIcon icon={faBars} className="text-xl" />
                </button>
              )}
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                {iconsReady && (
                  <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <h1 className="text-2xl font-medium text-gray-800">Users</h1>
            <button
              onClick={() => openUserModal()}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              {iconsReady && <FontAwesomeIcon icon={faPlus} className="mr-2" />}
              Yeni Kullanıcı Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arama
                </label>
                <input
                  type="text"
                  name="search"
                  placeholder="Ad veya Email"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Hepsi</option>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users List */}
          <div className="bg-white rounded-lg shadow">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Kullanıcılar yükleniyor...</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Ad Soyad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      E-Posta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Eylemler
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {user.full_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                                                    ${
                                                      user.status === 'active'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                    }`}
                        >
                          {user.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {iconsReady && (
                          <>
                            <button
                              onClick={() => openUserModal(user.id)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                              title="Edit User"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            {user.status === 'active' && (
                              <button
                                onClick={() => openInactivateDialog(user.id)}
                                className="text-red-600 hover:text-red-900 mr-4"
                                title="Inactivate User"
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </button>
                            )}
                            <button
                              onClick={() => openDeleteDialog(user.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete User"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                                          ${
                                            currentPage === 1
                                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                              : 'bg-white text-gray-700 hover:bg-gray-50'
                                          }`}
                  >
                    Önceki
                  </button>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={usersPerPage}
                      onChange={handleUsersPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                                          ${
                                            currentPage === totalPages
                                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                              : 'bg-white text-gray-700 hover:bg-gray-50'
                                          }`}
                  >
                    Sonraki
                  </button>
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 mr-4">
                    Görüntülenen{' '}
                    <span className="font-medium">{indexOfFirstUser + 1}</span>{' '}
                    -{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastUser, filteredUsers.length)}
                    </span>{' '}
                    <span className="font-medium">{filteredUsers.length}</span>{' '}
                    sonuç
                  </p>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={usersPerPage}
                      onChange={handleUsersPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium
                                                ${
                                                  currentPage === 1
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                                }`}
                    >
                      {iconsReady && (
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="h-4 w-4"
                        />
                      )}
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                                                            ${
                                                              currentPage ===
                                                              pageNumber
                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium
                                                ${
                                                  currentPage === totalPages
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                                }`}
                    >
                      {iconsReady && (
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="h-4 w-4"
                        />
                      )}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Add the UserModal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={selectedUser}
        onSave={handleSaveUser}
      />

      <Modal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Confirm Inactivation
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Are you sure you want to inactivate this user? This action will
            prevent the user from accessing the system.
          </p>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => setShowConfirmDialog(false)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleInactivate}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Inactivate
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Confirm Delete
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteDialog(false)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Users;
